import { Accordion, ActionIcon, Box, Button, Divider, Flex, Group, Text } from '@mantine/core';
import React, { ReactElement } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { FormField } from 'src/non-financial-data/components/FormField';
import {
  DynamicUiData,
  FieldWithValue,
  FormValuesType,
  UiEntityList,
  UiField,
  UiFieldRow,
  UiItem,
  UiSection,
} from './types';

export default class DynamicUiRenderer {
  form: UseFormReturnType<FormValuesType>;
  uiData: DynamicUiData;

  constructor(form: UseFormReturnType<FormValuesType>, uiData: DynamicUiData) {
    this.form = form;
    this.uiData = uiData;
    this.renderItem = this.renderItem.bind(this);
  }

  renderItem(item: UiItem) {
    if (item.type === 'UI_ENTITY') {
      return this.renderSection(item);
    }

    if (item.type === 'FIELD_ROW') {
      return this.renderFieldRow(item);
    }

    if (item.type === 'FIELD') {
      return this.renderField(item);
    }

    if (item.type === 'SEPARATOR') {
      return <Divider key={item.uniqueKey} my={'sm'} />;
    }

    if (item.type === 'ENTITY_LIST') {
      return this.renderEntityList(item);
    }

    return null;
  }

  private renderEntityList(entityList: UiEntityList) {
    const formListValues = this.form.getValues()[entityList.entityType];
    if (!formListValues || !Array.isArray(formListValues) || !this.uiData) {
      return null;
    }

    const renderUiFields = (
      children: (UiFieldRow | UiField)[],
      entityIndex: number,
    ): (ReactElement | null)[] => {
      return children
        .map((uiItem, index) => {
          if (uiItem.type === 'FIELD_ROW') {
            return (
              <Group key={`FIELD_ROW.${index}`}>
                {renderUiFields(uiItem.children, entityIndex)}
              </Group>
            );
          }
          return this.renderField(uiItem, entityIndex);
        })
        .flatMap(i => i);
    };

    const renderedEntities = formListValues.map((fieldWithValue, entityIndex) => {
      return (
        <Flex mb={'md'} align={'center'} direction={'row'} key={entityIndex}>
          <ActionIcon
            variant="transparent"
            radius="xl"
            aria-label="Settings"
            color={'black'}
            mr={'md'}
          >
            <IconTrash
              onClick={() => this.form.removeListItem(entityList.entityType, entityIndex)}
            />
          </ActionIcon>
          <Box>{renderUiFields(entityList.children, entityIndex)}</Box>
        </Flex>
      );
    });

    const defaultFields = entityList.children.reduce((defaultFields, uiField) => {
      if (uiField.type === 'FIELD_ROW') {
        const innerFields = uiField.children.reduce((defaultInnerFields, uiInnerField) => {
          return { ...defaultInnerFields, [uiInnerField.entityField]: '' };
        }, {} as FieldWithValue);
        return { ...defaultFields, ...innerFields };
      }

      return { ...defaultFields, [uiField.entityField]: '' };
    }, {} as FieldWithValue);
    return (
      <Box
        key={`${entityList.type}${entityList.entityType}${entityList.children
          .map(c => c.type)
          .join()}`}
      >
        {renderedEntities}
        <Box>
          <Button
            onClick={() => {
              this.form.insertListItem(entityList.entityType, defaultFields);
            }}
          >
            {entityList.addButtonLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  private renderSection(section: UiSection) {
    return (
      <Accordion.Item role="region" key={section.label} value={section.label}>
        <Accordion.Control>
          <Text fw={600} size={'lg'}>
            {section.label}
          </Text>
        </Accordion.Control>
        <Accordion.Panel p={'md'}>{section.children.map(this.renderItem)}</Accordion.Panel>
      </Accordion.Item>
    );
  }

  private renderFieldRow(fieldRow: UiFieldRow) {
    return (
      <Group key={fieldRow.uniqueKey} grow preventGrowOverflow={false}>
        {fieldRow.children.map(field => this.renderField(field))}
      </Group>
    );
  }

  private renderField(field: UiField, index: number | undefined = undefined): ReactElement | null {
    const entityWithMetadata = this.uiData?.entities[field.entityType];
    if (!entityWithMetadata) {
      return null;
    }

    const parentFieldName = index !== undefined ? `${field.entityType}.${index}` : field.entityType;

    return (
      <FormField
        key={this.form.key(`${parentFieldName}.${field.entityField}`)}
        form={this.form}
        parentFieldName={parentFieldName}
        fieldName={field.entityField}
        label={field.label}
        entityMetadata={this.uiData?.ui.entitiesMetadata[field.entityType][field.entityField]}
      />
    );
  }
}
